import { Injectable } from '@angular/core';

import { TemplateVariableService } from './template-variable.service';
import {
  TEMPLATE_BLOCK_DATA_ATTRIBUTE,
  TEMPLATE_BLOCK_LABEL_ATTRIBUTE
} from '../shared/components/c-rich-text/template-block.plugin';

/**
 * Corpo do template preparado para o editor.
 *
 * `editable` é o que o CKEditor recebe: só o conteúdo do `<body>`, com cada
 * trecho fixo trocado por um marcador `<span data-cred-block="N">`. O resto é o
 * que o editor nunca vê e volta intacto no `merge`.
 */
export interface ILockedTemplateBody {
  prefix: string;
  suffix: string;
  editable: string;
  blocks: string[];
  labels: string[];
  /**
   * O que o editor mostra no lugar de cada trecho fixo. Só exibição: o save usa
   * `blocks`. `null` = mostra o rótulo 🔒 (ex.: `{{#if}}`, que não tem conteúdo
   * próprio); `''` = não mostra nada (comentário, `<style>`: também não
   * aparecem no e-mail).
   */
  previews: (string | null)[];
}

/**
 * Deixa o usuário editar o TEXTO do corpo do e-mail sem que o CKEditor destrua a
 * estrutura do template.
 *
 * Os templates gravados são HTML escrito à mão: `{{#each}}` entre `<tbody>` e
 * `<tr>`, estilo inline, `<head>`, comentários. Nada disso sobrevive ao editor
 * (medido em 2026-09-16: `Parceiro.Transfer` perdia os `{{#each}}` e saía com a
 * tabela vazia). Em vez de ensinar o editor a representar isso, esses trechos
 * são recortados antes e devolvidos byte a byte depois:
 *
 *   split(): HTML do banco -> { editable, blocos fixos }
 *   o usuário edita `editable` (texto, parágrafos, negrito, cor...)
 *   merge(): editable editado + blocos fixos -> HTML para a API
 *
 * O trabalho é feito na string, de propósito: o parser HTML do navegador
 * (DOMParser) move o texto `{{#each}}` que está dentro de `<tbody>` para fora
 * da tabela — exatamente o estrago que se quer evitar.
 */
@Injectable({
  providedIn: 'root'
})
export class TemplateBodyLockService {
  /**
   * Cobre todos os caracteres da string (o `<` solto no fim garante isso), então
   * `tokens.join('')` é sempre igual à entrada.
   */
  private readonly TOKEN_REGEX = /<!--[\s\S]*?-->|<[^>]*>|[^<]+|</g;

  /** Tags cujo conteúdo inteiro vira um trecho fixo, com o fechamento. */
  private readonly LOCKED_ELEMENT_REGEX =
    /^<(table|figure|style|script|head|title)\b/i;

  /**
   * Expressões Handlebars que não são variável simples: blocos (`{{#if}}`,
   * `{{/if}}`, `{{else}}`, `{{^x}}`), comentários, parciais e `{{{x}}}`.
   * As variáveis `{{X}}` ficam de fora — viram chip no `maskHtml`.
   */
  private readonly HANDLEBARS_BLOCK_REGEX =
    /\{\{\{[^{}]*\}\}\}|\{\{~?\s*(?:[#/^!>]|else\b)[^{}]*\}\}/g;

  private readonly BLOCK_SPAN_REGEX = new RegExp(
    `<span[^>]*\\s${TEMPLATE_BLOCK_DATA_ATTRIBUTE}="(\\d+)"[^>]*>[\\s\\S]*?</span>`,
    'g'
  );

  /** Mesmo padrão do chip em `TemplateVariableService` (formato atual e legado). */
  private readonly VARIABLE_SPAN_REGEX =
    /<span[^>]*\sdata-(?:cred-var|mention)="[^"]*"[^>]*>[\s\S]*?<\/span>/g;

  /**
   * Parágrafo que só contém marcadores de trecho fixo e `<br>`. É o `<p>` que o
   * CKEditor cria em volta do que estava solto no `<body>` (ex.: `{{#if}}` entre
   * dois parágrafos, ou `<br><table>…</table><br><br>`) — no `merge` ele sai e o
   * conteúdo volta solto, como no original.
   */
  private readonly BLOCK_ONLY_PARAGRAPH_REGEX = new RegExp(
    `<p>((?:\\s|&nbsp;|<br>|<span[^>]*\\s${TEMPLATE_BLOCK_DATA_ATTRIBUTE}="\\d+"[^>]*>[^<]*</span>)*)</p>`,
    'g'
  );

  /** Marcador interno do `merge` — não pode existir num template real. */
  private readonly KEEP_REGEX = /\u0000(\d+)\u0000/g;

  constructor(private _templateVariableService: TemplateVariableService) {}

  split(html: string | null | undefined): ILockedTemplateBody {
    const { prefix, content, suffix } = this.splitFrame(html ?? '');
    const blocks: string[] = [];
    const labels: string[] = [];
    const previews: (string | null)[] = [];

    const lock = (
      chunk: string,
      label: string,
      preview: string | null = null
    ): string => {
      blocks.push(chunk);
      labels.push(label);
      previews.push(preview);

      // O rótulo vai também como conteúdo: com o span vazio o CKEditor trata o
      // espaço vizinho como início/fim de linha e o descarta
      // ("contrato: 123. Assinar RPA" virava "contrato: 123.Assinar RPA").
      return `<span ${TEMPLATE_BLOCK_DATA_ATTRIBUTE}="${
        blocks.length - 1
      }" ${TEMPLATE_BLOCK_LABEL_ATTRIBUTE}="${this.encodeAttribute(
        label
      )}">${this.encodeAttribute(label)}</span>`;
    };

    const tokens = content.match(this.TOKEN_REGEX) ?? [];
    let editable = '';

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];

      if (token.startsWith('<!--')) {
        editable += lock(token, 'Código fixo', '');
        continue;
      }

      const element = token.match(this.LOCKED_ELEMENT_REGEX)?.[1]?.toLowerCase();

      if (element) {
        const end = this.findClosingToken(tokens, i, element);
        const chunk = tokens.slice(i, end + 1).join('');
        const visible = element === 'table' || element === 'figure';

        editable += lock(
          chunk,
          this.labelForElement(element, chunk),
          visible ? this.previewHtml(chunk) : ''
        );
        i = end;
        continue;
      }

      // Sem plugin de imagem nem de linha horizontal no bundle: o editor as
      // descartaria.
      if (/^<img\b/i.test(token)) {
        editable += lock(token, 'Imagem', this.previewHtml(token));
        continue;
      }

      if (/^<hr\b/i.test(token)) {
        editable += lock(token, 'Linha divisória', this.previewHtml(token));
        continue;
      }

      // Link com variável no endereço (`<a href="{{EmailLink}}">`): no editor a
      // variável viraria atributo do texto do link, e apagar o texto levaria a
      // variável junto. Trava o link inteiro, texto incluído.
      if (/^<a\b[^>]*\{\{/i.test(token)) {
        const end = this.findClosingToken(tokens, i, 'a');
        const chunk = tokens.slice(i, end + 1).join('');
        const text = this.decodeText(chunk.replace(/<[^>]*>/g, '').trim());

        editable += lock(
          chunk,
          text ? `Link: ${text}` : 'Link',
          this.previewHtml(chunk)
        );
        i = end;
        continue;
      }

      // Qualquer outra tag vai para o editor.
      if (token.startsWith('<')) {
        editable += token;
        continue;
      }

      // Bloco Handlebars fora de tabela (`{{#each Batchs}}` em volta de
      // "Lote: X" + tabela nos templates de banco, `{{#if UserMessage}}` no
      // RPA): trava do `{{#...}}` até o `{{/...}}` correspondente e mostra o
      // conteúdo uma vez. Sem isso o início e o fim viravam dois 🔒 soltos.
      const block = this.findBlock(tokens, i);

      if (block) {
        editable += this.lockHandlebarsBlocks(block.before, lock);

        const preview = this.previewHtml(block.chunk);
        editable += lock(
          block.chunk,
          this.labelForHandlebars(block.open),
          this.hasVisibleContent(preview) ? preview : null
        );

        // O que vem depois do `{{/...}}` volta para a fila e é processado
        // normalmente.
        tokens.splice(i + 1, tokens.length - i - 1, ...block.rest);
        continue;
      }

      editable += this.lockHandlebarsBlocks(token, lock);
    }

    return {
      prefix,
      suffix,
      editable: this.unwrapListParagraphs(editable),
      blocks,
      labels,
      previews
    };
  }

  /**
   * Versão de exibição de um trecho fixo: o HTML original sem os blocos
   * Handlebars e com as variáveis trocadas pelo chip. Numa tabela com
   * `{{#each}}`, a linha do laço aparece uma vez, com os chips de cada coluna.
   *
   * Os blocos saem porque o `{{#each}}` entre `<tbody>` e `<tr>` seria jogado
   * para fora da tabela pelo parser do navegador. Variável em atributo
   * (`href="{{EmailLink}}"`) só é apagada.
   */
  private previewHtml(chunk: string): string {
    return chunk
      .split(/(<!--[\s\S]*?-->|<[^>]*>)/)
      .map((part, index) => {
        if (index % 2 === 1) {
          return part.startsWith('<!--')
            ? ''
            : part.replace(/\{\{\{?[^{}]*\}?\}\}/g, '');
        }

        return part
          .replace(this.HANDLEBARS_BLOCK_REGEX, (expression) => {
            const triple = expression.match(/^\{\{\{\s*([^{}]+?)\s*\}\}\}$/);
            return triple ? this.previewChip(triple[1]) : '';
          })
          .replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, expression: string) =>
            this.previewChip(expression)
          );
      })
      .join('');
  }

  private previewChip(expression: string): string {
    // `{{../NFe}}` (campo do laço de fora) mostra o rótulo de `NFe`.
    const name = expression.replace(/^(\.\.\/)+/, '');

    return `<span class="cred-var">${this.encodeAttribute(
      this.variableLabel(name)
    )}</span>`;
  }

  /** Trava cada bloco Handlebars solto (`{{#if}}`, `{{/if}}`...) do texto. */
  private lockHandlebarsBlocks(
    text: string,
    lock: (chunk: string, label: string) => string
  ): string {
    return text.replace(this.HANDLEBARS_BLOCK_REGEX, (expression) =>
      lock(expression, this.labelForHandlebars(expression))
    );
  }

  /**
   * Primeiro `{{#each}}`/`{{#if}}`/`{{#unless}}`/`{{#with}}` do texto do token
   * `start` e o `{{/...}}` que o fecha, mesmo que esteja vários tokens depois
   * (com tabela no meio). `null` se não há bloco ou se ele não fecha — aí cada
   * marcador vira um 🔒 solto, como antes.
   */
  private findBlock(
    tokens: string[],
    start: number
  ): { before: string; open: string; chunk: string; rest: string[] } | null {
    const token = tokens[start];
    const open = token.match(/\{\{~?\s*#(each|if|unless|with)\b[^{}]*\}\}/);

    if (open?.index === undefined) return null;

    const helper = open[1];
    const text = token.slice(open.index) + tokens.slice(start + 1).join('');
    const marker = new RegExp(
      `\\{\\{~?\\s*#${helper}\\b[^{}]*\\}\\}|\\{\\{~?\\s*\\/${helper}\\s*~?\\}\\}`,
      'g'
    );
    let depth = 0;

    for (const match of text.matchAll(marker)) {
      depth += /^\{\{~?\s*#/.test(match[0]) ? 1 : -1;

      if (depth === 0) {
        const end = (match.index ?? 0) + match[0].length;

        return {
          before: token.slice(0, open.index),
          open: open[0],
          chunk: text.slice(0, end),
          rest: text.slice(end).match(this.TOKEN_REGEX) ?? []
        };
      }
    }

    return null;
  }

  private hasVisibleContent(html: string): boolean {
    return (
      /<(img|hr|table)\b|class="cred-var"/i.test(html) ||
      this.decodeText(html.replace(/<[^>]*>/g, '')).trim() !== ''
    );
  }

  /**
   * Recebe o HTML que saiu do editor (chips e marcadores ainda no lugar) e
   * devolve o template pronto para a API.
   */
  merge(editorHtml: string | null | undefined, body: ILockedTemplateBody): string {
    const kept: string[] = [];
    const keep = (value: string): string => {
      kept.push(value);
      return `\u0000${kept.length - 1}\u0000`;
    };

    let html = (editorHtml ?? '').replace(
      this.BLOCK_ONLY_PARAGRAPH_REGEX,
      (paragraph: string, content: string) =>
        content.includes(TEMPLATE_BLOCK_DATA_ATTRIBUTE)
          ? (content.match(/<br>|<span[^>]*>[^<]*<\/span>/g) ?? []).join('')
          : paragraph
    );

    html = html.replace(this.BLOCK_SPAN_REGEX, (_match, id: string) =>
      keep(body.blocks[Number(id)] ?? '')
    );

    html = html.replace(this.VARIABLE_SPAN_REGEX, (span: string) =>
      keep(this._templateVariableService.unmaskHtml(span))
    );

    // O CKEditor grava como `&nbsp;` o espaço colado a um widget (para não o
    // perder); no template ele volta a ser um espaço comum, como no original.
    html = html
      .replace(/&nbsp;(\u0000\d+\u0000)/g, ' $1')
      .replace(/(\u0000\d+\u0000)&nbsp;/g, '$1 ');

    // Daqui para frente só sobrou o que o usuário digitou.
    html = this.escapeTypedBraces(html);

    html = html.replace(this.KEEP_REGEX, (_match, index: string) =>
      kept[Number(index)]
    );

    return body.prefix + html + body.suffix;
  }

  /**
   * Trechos fixos que sumiram (widget apagado) ou foram duplicados (widget
   * copiado e colado) no HTML do editor.
   */
  changedBlocks(
    editorHtml: string | null | undefined,
    body: ILockedTemplateBody
  ): string[] {
    const counts = new Map<number, number>();

    for (const match of (editorHtml ?? '').matchAll(this.BLOCK_SPAN_REGEX)) {
      const id = Number(match[1]);
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }

    return body.labels.filter((_label, id) => counts.get(id) !== 1);
  }

  /**
   * Separa o que fica fora do `<body>` (doctype, `<html>`, `<head>`) — o
   * editor não tem onde colocar isso e o descartaria.
   */
  private splitFrame(html: string): {
    prefix: string;
    content: string;
    suffix: string;
  } {
    const open = html.match(/<body\b[^>]*>/i);
    const closeIndex = html.search(/<\/body\s*>/i);

    if (open?.index === undefined || closeIndex < open.index) {
      return { prefix: '', content: html, suffix: '' };
    }

    const contentStart = open.index + open[0].length;

    return {
      prefix: html.slice(0, contentStart),
      content: html.slice(contentStart, closeIndex),
      suffix: html.slice(closeIndex)
    };
  }

  /**
   * `<li><p>texto</p></li>` -> `<li>texto</li>`. O plugin de lista do CKEditor 37
   * não aceita bloco dentro do item: transforma o item num parágrafo solto e o
   * marcador some do e-mail (visto nas "Orientações" de `Parceiro.Transfer`).
   * Só desembrulha quando o item tem exatamente um parágrafo.
   */
  private unwrapListParagraphs(html: string): string {
    return html.replace(
      /(<li\b[^>]*>)\s*<p\b[^>]*>((?:(?!<\/?p\b)[\s\S])*?)<\/p>\s*(<\/li>)/gi,
      '$1$2$3'
    );
  }

  /** Índice do token que fecha `element`, respeitando aninhamento. */
  private findClosingToken(
    tokens: string[],
    start: number,
    element: string
  ): number {
    const opening = new RegExp(`^<${element}\\b`, 'i');
    const closing = new RegExp(`^</${element}\\s*>`, 'i');
    let depth = 0;

    for (let i = start; i < tokens.length; i++) {
      if (opening.test(tokens[i]) && !tokens[i].endsWith('/>')) depth++;
      if (closing.test(tokens[i])) depth--;
      if (depth === 0) return i;
    }

    // Sem fechamento: trava o resto, que é o lado seguro.
    return tokens.length - 1;
  }

  private labelForElement(element: string, chunk: string): string {
    if (element === 'table' || element === 'figure') {
      const list = chunk.match(/\{\{~?\s*#each(?:Table)?\s+([A-Za-z0-9_.]+)/);

      return list ? `Tabela: ${this.variableLabel(list[1])}` : 'Tabela';
    }

    if (element === 'style' || element === 'head') return 'Estilo do e-mail';
    if (element === 'title') return 'Título do e-mail';

    return 'Código fixo';
  }

  private labelForHandlebars(expression: string): string {
    const triple = expression.match(/^\{\{\{\s*([^{}]+?)\s*\}\}\}$/);
    if (triple) return this.variableLabel(triple[1]);

    const open = expression.match(/^\{\{~?\s*#(\w+)\s*([^{}]*?)\s*~?\}\}$/);
    if (open) {
      const [, helper, argument] = open;
      const subject = this.variableLabel(argument);

      if (helper === 'if') return `Se houver: ${subject}`;
      if (helper === 'unless') return `Se não houver: ${subject}`;
      if (helper === 'each') return `Lista: ${subject}`;

      return `${helper}: ${subject}`;
    }

    const close = expression.match(/^\{\{~?\s*\/(\w+)/);
    if (close) {
      if (close[1] === 'each') return 'Fim da lista';
      if (close[1] === 'if' || close[1] === 'unless') return 'Fim da condição';

      return 'Fim do bloco';
    }

    if (/^\{\{~?\s*else\b/.test(expression)) return 'Senão';

    return 'Código fixo';
  }

  /** Rótulo de um argumento de helper; expressões compostas ficam cruas. */
  private variableLabel(argument: string): string {
    return /^[A-Za-z0-9_.]+$/.test(argument)
      ? this._templateVariableService.label(argument)
      : argument;
  }

  /**
   * `{` e `}` digitados viram entidade: aparecem iguais no e-mail, mas o
   * Handlebars não os enxerga. Sem isso, um `{{` digitado por engano vira
   * expressão — nome desconhecido some do e-mail e bloco malformado derruba o
   * envio (`CompileTemplate` não trata a exceção).
   */
  private escapeTypedBraces(html: string): string {
    return html
      .split(/(<[^>]*>)/)
      .map((part, index) =>
        index % 2 === 1
          ? part
          : part.replace(/\{/g, '&#123;').replace(/\}/g, '&#125;')
      )
      .join('');
  }

  /** Só as entidades comuns — o resultado vai para um rótulo, não para o HTML. */
  private decodeText(value: string): string {
    return value
      .replace(/&nbsp;/g, ' ')
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  private encodeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
